import axios from "axios";
import { getImpersonationHeader } from "./impersonation";
import { resolveTenantSlug } from "./tenant";
import { getToken, clearToken } from "./tokenStore";

// Re-export so the dozens of existing
// `import { resolveTenantSlug } from "../lib/api"` callsites keep working.
export { resolveTenantSlug };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const FILE_BASE = `${BACKEND_URL}`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const slug = resolveTenantSlug();
  if (slug) config.headers["X-Tenant-Slug"] = slug;
  const impUid = getImpersonationHeader();
  if (impUid) config.headers["X-Impersonate-Seller-User-Id"] = impUid;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // ONLY clear the slot that issued this request, not every tenant's
      // session — a 401 on Tenant B should never sign Tenant A out.
      clearToken({ broadcast: false });
    }
    return Promise.reject(err);
  }
);

// Media URL passthrough.
//
// The backend absolutises every media field on the way out via the
// AbsolutizeMediaMiddleware — every image_url, banner_url, avatar,
// thumbnail, etc. returned by /api/* is already a full https:// URL
// pointing at the deployment's configured AWS_PUBLIC_MEDIA_HOST.
//
// So `imgUrl()` should be a no-op passthrough in practice. The only
// reason we keep it is for legacy call sites that hand it a bare
// value (e.g. from local state after an upload but before the server
// echo). If a bare key still leaks in, we absolutise against the same
// host name Emergent uses so preview keeps working. This can be
// removed entirely once every upload response returns an absolute URL.
export function imgUrl(s) {
  if (!s || typeof s !== "string") return "";
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:") || s.startsWith("/")) return s;
  // Legacy fallback for bare keys — this branch should be dead code.
  return `https://media.localappstore.in/${s}`;
}

export const money = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
